import * as repo from '@/db/repositories'

/**
 * Caché en memoria de object URLs de portadas, compartido entre todas las
 * instancias de `BookCover` y el store de biblioteca.
 *
 * Sin esto, cada vez que un `BookCover` se monta (al filtrar, reordenar o
 * paginar) reléa la portada de IndexedDB → N lecturas individuales que
 * congelan la UI con 50-200 libros.
 *
 * El caché guarda el objectURL ya creado; revocarlo solo al eliminar un
 * libro (invalidación manual vía `invalidateCoverCache`).
 */

/** Caché bookId → objectURL. Valor `''` significa "sin portada" (cacheado
 *  para no releer sabiendo que no hay). */
const coverUrlCache = new Map<string, string>()
/** Promesas en vuelo para deduplicar peticiones concurrentes del mismo id. */
const pendingCovers = new Map<string, Promise<string | null>>()

/**
 * Devuelve el objectURL cacheado de una portada, o lo lee de IndexedDB y lo
 * cachea. Deduplica peticiones concurrentes del mismo id (varios BookCover
 * montándose a la vez no disparan N lecturas).
 */
export async function getCoverUrl(bookId: string): Promise<string | null> {
  const cached = coverUrlCache.get(bookId)
  if (cached !== undefined) return cached || null
  const pending = pendingCovers.get(bookId)
  if (pending) return pending
  const p = (async () => {
    const blob = await repo.getBookCover(bookId)
    const url = blob ? URL.createObjectURL(blob) : ''
    // Guardar incluso '' para no releer sabiendo que no hay portada.
    coverUrlCache.set(bookId, url)
    pendingCovers.delete(bookId)
    return url || null
  })()
  pendingCovers.set(bookId, p)
  return p
}

/**
 * Invalida una entrada del caché (p. ej. al borrar un libro o cambiar su
 * portada). Revoca el objectURL si existía.
 */
export function invalidateCoverCache(bookId: string): void {
  const url = coverUrlCache.get(bookId)
  if (url) URL.revokeObjectURL(url)
  coverUrlCache.delete(bookId)
}

/** Invalida todas las entradas (p. ej. al cerrar sesión). */
export function clearCoverCache(): void {
  for (const url of coverUrlCache.values()) {
    if (url) URL.revokeObjectURL(url)
  }
  coverUrlCache.clear()
}
